import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";



function Profile() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    companyName: "",
    abn: "",
  });

  // ✅ NEW: status flags from DB
  const [status, setStatus] = useState({
    emailVerified: false,
    businessVerified: false, // we’ll treat ABN presence as “business on file” for now
    identityVerified: false,
  });

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  // ===== Fetch user profile (READ-ONLY) =====
  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        console.warn("⚠️ No token found — redirecting to login");
        navigate("/login");
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/profile`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          setMessage(`Failed to load profile: ${res.status}`);
          setLoading(false);
          return;
        }

        const data = await res.json();
        console.log("✅ Profile API response:", data);

        if (data?.success && data?.user) {
          const u = data.user;

          // Set profile fields
          setFormData({
            name: u.name || "",
            email: u.email || "",
            phone: u.phone || "",
            companyName: u.companyName || "",
            abn: u.abn || "",
          });

          // ✅ Set verification status from your DB columns
          const emailVerified = Number(u.email_verified) === 1;
          const identityVerified =
            Number(u.licence_verified) === 1 || String(u.verified_id || "") === "Y";

          // You don't have abn_verified column (yet), so use ABN present as "business on file"
          // Later: replace this with Number(u.abn_verified) === 1 when you add it
          const businessVerified = !!(u.abn && String(u.abn).trim().length > 0);

          setStatus({
            emailVerified,
            businessVerified,
            identityVerified,
          });
        } else {
          setMessage("Failed to load profile data");
        }
      } catch (err) {
        console.error("❌ Error loading profile:", err);
        setMessage("Server error while loading profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate]);

  if (loading) {
    return (
      <p style={{ textAlign: "center", marginTop: "50px" }}>Loading profile...</p>
    );
  }

  const statusItems = [
    { label: "Email Verified", ok: status.emailVerified },
    { label: "Business On File", ok: status.businessVerified },
    { label: "Identity Verified", ok: status.identityVerified },
  ];

  return (
    <div style={pageWrap}>
      <div style={card}>
        {/* Header */}
        <div style={headerRow}>
          <div>
            <h2 style={title}>My Profile</h2>
            <p style={subtitle}>
              Your business details are protected. To change anything, contact
              support.
            </p>
          </div>

          {/* Status chips */}
          <div style={chipWrap}>
            {statusItems.map((s) => (
              <span
                key={s.label}
                style={{
                  ...chip,
                  ...(s.ok ? chipGood : chipPending),
                }}
                title={s.ok ? "Verified" : "Pending"}
              >
                {s.ok ? "✔" : "⏳"} {s.label}
              </span>
            ))}
          </div>
        </div>

        {message && (
          <div
            style={{
              ...alert,
              ...(message.includes("✅") ? alertGood : alertBad),
            }}
          >
            {message}
          </div>
        )}

        {/* Content */}
        <div style={grid}>
          {/* Left: Profile */}
          <div style={section}>
            <div style={sectionHeader}>
              <div style={sectionTitle}>Business Details</div>
              <div style={sectionHint}>Read-only</div>
            </div>

            <Field label="Name" value={formData.name} />
            <Field label="Email" value={formData.email} />
            <Field label="Phone" value={formData.phone} />
            <Field label="Company Name" value={formData.companyName} />
            <Field label="ABN" value={formData.abn} />

            <div style={miniNote}>
              🔒 These fields are locked to protect verified identities.
            </div>
          </div>

          {/* Right: Support / Next steps */}
          <div style={section}>
            <div style={sectionHeader}>
              <div style={sectionTitle}>Support</div>
              <div style={sectionHint}>Fast help</div>
            </div>

            <div style={supportBox}>
              <div style={supportTitle}>Need to update your details?</div>
              <div style={supportText}>
                Send a support request and we’ll update it manually after
                confirming your identity.
              </div>

              <div style={supportList}>
                <div style={supportItem}>
                  • Include your account email and what you want changed
                </div>
                <div style={supportItem}>
                  • ABN changes require extra verification
                </div>
              </div>

              <div style={supportFooter}>
                <span style={supportPill}>Recommended</span>
                <span style={supportSmall}>Manual updates improve trust</span>
              </div>
            </div>

            <div style={sectionDivider} />

            <div style={nextSteps}>
              <div style={nextTitle}>Next steps</div>
              <div style={nextItem}>• Add “Verified” badge to listings</div>
              <div style={nextItem}>• Add admin-only profile update tool</div>
              <div style={nextItem}>• Log manual changes in activity logs</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Small Field component
function Field({ label, value }) {
  return (
    <div style={fieldRow}>
      <div style={fieldLabel}>{label}</div>
      <input value={value || "-"} disabled style={readOnlyInput} />
    </div>
  );
}

/* =========================
   Styles
========================= */

const pageWrap = {
  maxWidth: "980px",
  margin: "40px auto",
  padding: "0 16px",
};

const card = {
  backgroundColor: "#fff",
  borderRadius: "16px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  border: "1px solid rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  padding: "28px",
  borderBottom: "1px solid rgba(0,0,0,0.06)",
  background:
    "linear-gradient(180deg, rgba(76,175,80,0.10) 0%, rgba(255,255,255,1) 65%)",
};

const title = {
  margin: 0,
  fontSize: "26px",
  letterSpacing: "-0.2px",
};

const subtitle = {
  margin: "8px 0 0",
  color: "#555",
  fontSize: "14px",
  lineHeight: 1.5,
  maxWidth: "520px",
};

const chipWrap = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  justifyContent: "flex-end",
};

const chip = {
  fontSize: "12px",
  padding: "8px 10px",
  borderRadius: "999px",
  border: "1px solid rgba(0,0,0,0.08)",
  background: "#fff",
  color: "#333",
  whiteSpace: "nowrap",
};

const chipGood = {
  border: "1px solid rgba(76,175,80,0.35)",
  backgroundColor: "rgba(76,175,80,0.10)",
};

const chipPending = {
  border: "1px solid rgba(255,193,7,0.45)",
  backgroundColor: "rgba(255,193,7,0.12)",
};

const alert = {
  margin: "18px 28px 0",
  padding: "12px 14px",
  borderRadius: "10px",
  fontSize: "14px",
};

const alertGood = {
  backgroundColor: "rgba(76,175,80,0.12)",
  border: "1px solid rgba(76,175,80,0.35)",
  color: "#1b5e20",
};

const alertBad = {
  backgroundColor: "rgba(244,67,54,0.08)",
  border: "1px solid rgba(244,67,54,0.25)",
  color: "#b71c1c",
};

const grid = {
  display: "grid",
  gridTemplateColumns: "1.1fr 0.9fr",
  gap: "18px",
  padding: "24px 28px 28px",
};

const section = {
  backgroundColor: "#fff",
  borderRadius: "14px",
  border: "1px solid rgba(0,0,0,0.06)",
  padding: "18px",
};

const sectionHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "14px",
};

const sectionTitle = {
  fontWeight: 800,
  fontSize: "14px",
  color: "#222",
};

const sectionHint = {
  fontSize: "12px",
  color: "#666",
  backgroundColor: "rgba(0,0,0,0.04)",
  padding: "6px 10px",
  borderRadius: "999px",
};

const fieldRow = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  marginBottom: "12px",
};

const fieldLabel = {
  fontSize: "12px",
  color: "#666",
  fontWeight: 700,
};

const readOnlyInput = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "10px",
  border: "1px solid rgba(0,0,0,0.12)",
  backgroundColor: "rgba(0,0,0,0.03)",
  color: "#222",
  cursor: "not-allowed",
};

const miniNote = {
  marginTop: "8px",
  fontSize: "12.5px",
  color: "#666",
  lineHeight: 1.5,
  backgroundColor: "rgba(0,0,0,0.03)",
  padding: "10px 12px",
  borderRadius: "10px",
  border: "1px solid rgba(0,0,0,0.06)",
};

const supportBox = {
  padding: "16px",
  borderRadius: "14px",
  border: "1px solid rgba(76,175,80,0.22)",
  backgroundColor: "rgba(76,175,80,0.08)",
};

const supportTitle = {
  fontWeight: 900,
  fontSize: "14px",
  color: "#1b5e20",
  marginBottom: "6px",
};

const supportText = {
  fontSize: "13px",
  color: "#2e2e2e",
  lineHeight: 1.55,
};

const supportList = {
  marginTop: "10px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

const supportItem = {
  fontSize: "12.5px",
  color: "#333",
};

const supportFooter = {
  marginTop: "12px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const supportPill = {
  fontSize: "12px",
  fontWeight: 800,
  padding: "6px 10px",
  borderRadius: "999px",
  backgroundColor: "rgba(76,175,80,0.20)",
  border: "1px solid rgba(76,175,80,0.30)",
  color: "#1b5e20",
};

const supportSmall = {
  fontSize: "12px",
  color: "#444",
};

const sectionDivider = {
  height: "1px",
  backgroundColor: "rgba(0,0,0,0.06)",
  margin: "16px 0",
};

const nextSteps = {
  padding: "4px 2px",
};

const nextTitle = {
  fontSize: "13px",
  fontWeight: 900,
  marginBottom: "8px",
  color: "#222",
};

const nextItem = {
  fontSize: "12.5px",
  color: "#444",
  marginBottom: "6px",
};

// ✅ Responsive (simple)
if (typeof window !== "undefined") {
  const mq = window.matchMedia("(max-width: 880px)");
  if (mq.matches) {
    grid.gridTemplateColumns = "1fr";
  }
}

export default Profile;